/**
 * Reducer da chamada ATIVA do webphone deste agente.
 *
 * Extraído de `webphone-sticky.tsx` na #407: a lógica vivia dentro de um
 * `useCallback` inline, sem teste possível, e foi exatamente ali que o defeito
 * se escondeu. Segue o padrão canônico do módulo de tempo real — "1 reducer por
 * evento", explícito e verboso, de modo que cada gap vire erro de tipo ou teste
 * vermelho em vez de comportamento silencioso.
 *
 * **A regra que rege este arquivo:** o motor emite os eventos de voz para o
 * tenant INTEIRO. Nada aqui pode ancorar sem antes provar que a chamada é deste
 * ramal — a ausência dessa prova no ramo outbound é o bug da #407.
 */

export type ActiveCall = { uid: string; protocolo: string | null };

/**
 * Payload dos eventos `voice.*` do motor.
 *
 * Atenção à assimetria, capturada do `voiceami` da Minas Telecom em 21/08/2026:
 *
 * ```
 * { unique_id: "1787347296.74538", caller: "1040",
 *   extension: "79999547795", direction: "outbound", protocolo: "MINAS…" }
 * ```
 *
 * Em **outbound**, `caller` é o RAMAL que discou e `extension` é o número
 * destino. Em inbound os papéis se invertem, e quem identifica o agente é o
 * `answered_by_extension` do `voice.bridged`. Por isso cada direção tem seu
 * próprio campo de ancoragem — não existe um campo único "é meu".
 */
type VoiceEventData = {
  unique_id?: string;
  direction?: string;
  /** Outbound: ramal de origem. Inbound: número do cliente. */
  caller?: string;
  answered_by_extension?: string;
  protocolo?: string | null;
};

export type VoiceEvent = { event: string; data: unknown };

/**
 * Devolve o próximo estado da chamada ativa.
 *
 * Retorna a MESMA referência de `prev` quando o evento não é deste agente — o
 * chamador usa isso com `setState`, e identidade referencial evita re-render à
 * toa a cada evento de colega.
 */
export function reduceActiveCall(
  prev: ActiveCall | null,
  evt: VoiceEvent,
  myExtension: string | null,
): ActiveCall | null {
  const data = (evt.data ?? {}) as VoiceEventData;
  const uid = data.unique_id;
  if (!uid) return prev;

  switch (evt.event) {
    case 'voice.bridged':
      // Inbound: o ramal que atendeu sou eu → esta é a minha chamada.
      if (myExtension && data.answered_by_extension === myExtension) {
        return { uid, protocolo: data.protocolo ?? null };
      }
      return prev;

    case 'voice.dialing':
    case 'voice.ringing':
      // #407: outbound do MEU ramal — e agora isso é verificado.
      //
      // Antes o filtro era só `direction === 'outbound'`, e o motor emite pro
      // tenant inteiro: a discagem de qualquer colega sobrescrevia a chamada
      // ativa de todos os webphones abertos. Na Minas, uma agente em
      // atendimento passou a exibir o protocolo da ligação de outro ramal
      // discada 1,4s depois, e leu esse número pro cliente.
      //
      // `caller` ausente NÃO ancora: sem prova de que é minha, o certo é
      // ignorar. Ancorar por omissão é como o bug existia.
      if (
        data.direction === 'outbound' &&
        myExtension &&
        data.caller === myExtension
      ) {
        return { uid, protocolo: data.protocolo ?? null };
      }
      return prev;

    case 'voice.hangup':
    case 'voice.transferred':
      // Encerrou/transferiu A MINHA chamada ativa → limpa (uid + protocolo).
      return prev?.uid === uid ? null : prev;

    default:
      return prev;
  }
}
